import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '@/services/api';
import PageShell from '../../../Components/PageShell';
import AdminNav from '../AdminNav';
import EmailsTabs from './EmailsTabs';
import { RECIPIENT_STATUS_META, STATUS_META } from './campaignMeta';
import './EmailsPage.css';
import './DigestsPage.css';

/* The monthly release digest — read-only, by design.
 *
 * Digests are generated from the trade data by jobs/releaseDigest.js and every
 * recipient gets a different body, so there is nothing here to edit and no
 * single email to preview. This screen answers the one question worth asking
 * after a send: who got it, which of the two shapes they got, and what
 * happened to the message. Digest campaigns are excluded from the campaigns
 * list for the same reason.
 *
 * Two routes, one component: /admin/emails/digests lists the months,
 * /admin/emails/digests/:monthId is one month's recipients.
 */

const SHAPE_META = {
    own_news: { label: 'their companies', hint: 'named matches or saved-trader movement of their own' },
    quiet_month: { label: 'quiet month', hint: 'they have saved searches or traders, but nothing changed' },
    market: { label: 'market', hint: 'nothing saved — the re-engagement shape, led by the release' },
};

const fmtDate = (value) => (value
    ? new Date(value).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—');

const MonthList = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        api.get('admin/email/digests')
            .then((r) => setItems(r.items || []))
            .catch((e) => setError(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, []);

    return (
        <>
            <div className="admin-emails-header">
                <h1 className="admin-emails-title">Release digests</h1>
                <span className="admin-emails-count">{loading ? '' : `${items.length} release${items.length === 1 ? '' : 's'}`}</span>
            </div>
            <p className="admin-emails-blurb">
                One release of HMRC trade data per row, and what the monthly email did with it.
                Generated and sent by <code>jobs/releaseDigest.js</code> — there is nothing to edit here.
                A release showing <strong>not sent</strong> was generated but never mailed.
            </p>

            {error && <div className="admin-emails-error">Error loading digests: {error}</div>}

            {loading ? <div className="admin-emails-loading">Loading…</div> : (
                <div className="admin-emails-table-wrap">
                    <table className="admin-emails-table">
                        <thead>
                            <tr>
                                <th>Release</th>
                                <th>Status</th>
                                <th className="num">Recipients</th>
                                <th className="num">Sent</th>
                                <th className="num">Delivered</th>
                                <th className="num">Opened</th>
                                <th className="num">Bounced</th>
                                <th>Generated</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={8} className="admin-emails-empty">No release has been digested yet.</td></tr>
                            )}
                            {items.map((m) => {
                                const sent = m.status !== 'not_sent';
                                return (
                                    <tr
                                        key={m.month_id}
                                        onClick={() => sent && navigate(`/admin/emails/digests/${m.month_id}`)}
                                        className={sent ? '' : 'digest-row-unsent'}
                                    >
                                        <td>
                                            <div className="admin-emails-name">{m.label}</div>
                                            <div className="admin-emails-subject">
                                                {m.orgs} org{m.orgs === 1 ? '' : 's'} digested, {m.orgs_with_content} with news of their own
                                            </div>
                                        </td>
                                        <td>
                                            <span className={`badge ${sent ? (STATUS_META[m.status] || { badge: 'cold' }).badge : 'cold'}`}>
                                                {sent ? (STATUS_META[m.status] || { label: m.status }).label : 'not sent'}
                                            </span>
                                        </td>
                                        <td className="num">{sent ? m.recipients : '—'}</td>
                                        <td className="num">{sent ? m.sent : '—'}</td>
                                        <td className="num">{sent ? m.delivered : '—'}</td>
                                        <td className="num">{sent ? m.opened : '—'}</td>
                                        <td className="num">{sent ? (m.bounced + m.spam + m.failed) : '—'}</td>
                                        <td className="admin-emails-date">{fmtDate(m.generated_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

const MonthDetail = ({ monthId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [sending, setSending] = useState(false);
    const [notice, setNotice] = useState(null);
    const [tick, setTick] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        setLoading(true);
        api.get(`admin/email/digests/${monthId}`)
            .then(setData)
            .catch((e) => setError(e.response?.data?.error || e.message))
            .finally(() => setLoading(false));
    }, [monthId, tick]);

    const items = (data && data.items) || [];
    const shapeCount = (shape) => items.filter((r) => r.shape === shape).length;
    const status = data && data.status;
    const pending = items.filter((r) => r.status === 'pending').length;

    /* While the loop runs the rows change underneath us, so poll. It stops as
     * soon as the campaign leaves 'sending' — no interval left running on a
     * screen someone forgot to close. */
    useEffect(() => {
        if (status !== 'sending') return undefined;
        const id = setInterval(() => setTick((t) => t + 1), 4000);
        return () => clearInterval(id);
    }, [status]);

    const handleSend = async () => {
        const ok = window.confirm(
            `Send the ${data.label} digest to ${pending} recipient${pending === 1 ? '' : 's'}?\n\n`
            + `${shapeCount('own_news')} get their own companies, `
            + `${shapeCount('quiet_month')} a quiet month, `
            + `${shapeCount('market')} the market email.\n\n`
            + 'This cannot be undone.'
        );
        if (!ok) return;
        setSending(true);
        setNotice(null);
        try {
            const r = await api.post(`admin/email/campaigns/${data.campaign_id}/send`, { confirm: true });
            setNotice(r.dry_run
                ? 'Dry run started — rows are being processed, nothing is leaving the server.'
                : 'Sending started. This page updates as it progresses.');
            setTick((t) => t + 1);
        } catch (e) {
            setNotice(`Send failed: ${e.response?.data?.error || e.message}`);
        } finally {
            setSending(false);
        }
    };

    return (
        <>
            <div className="admin-emails-header">
                <h1 className="admin-emails-title">{data ? data.label : 'Release digest'}</h1>
                <span className="admin-emails-count">{loading ? '' : `${items.length} recipient${items.length === 1 ? '' : 's'}`}</span>
                <button type="button" className="btn btn-secondary admin-emails-new-btn" onClick={() => navigate('/admin/emails/digests')}>
                    Back to releases
                </button>
            </div>

            {!loading && items.length > 0 && (
                <p className="admin-emails-blurb">
                    {shapeCount('own_news')} {status === 'ready' ? 'get' : 'received'} their own companies,{' '}
                    {shapeCount('quiet_month')} had saved items but a quiet month, and{' '}
                    {shapeCount('market')} {status === 'ready' ? 'get' : 'got'} the market shape.
                </p>
            )}

            {/* The digest is generated, not written, so there is nothing to
                edit here — the only action is to send it, and only while it
                is still 'ready'. */}
            {!loading && status === 'ready' && (
                <div className="digest-send-panel">
                    <div>
                        <strong>Ready to send</strong>
                        <div className="digest-send-detail">
                            {pending} recipient{pending === 1 ? '' : 's'}, none mailed yet.
                            Suppressed and bounced addresses are dropped again at the moment of sending.
                        </div>
                    </div>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSend}
                        disabled={sending || pending === 0}
                    >
                        {sending ? 'Starting…' : `Send to ${pending}`}
                    </button>
                </div>
            )}

            {!loading && status === 'sending' && (
                <div className="digest-send-panel">
                    <div>
                        <strong>Sending…</strong>
                        <div className="digest-send-detail">{pending} still to go. This updates every few seconds.</div>
                    </div>
                </div>
            )}

            {notice && <div className="digest-notice">{notice}</div>}

            {error && <div className="admin-emails-error">Error loading digest: {error}</div>}

            {loading ? <div className="admin-emails-loading">Loading…</div> : (
                <div className="admin-emails-table-wrap">
                    <table className="admin-emails-table">
                        <thead>
                            <tr>
                                <th>Recipient</th>
                                <th>Organisation</th>
                                <th>Shape</th>
                                <th>Status</th>
                                <th className="num">Opens</th>
                                <th>Sent</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length === 0 && (
                                <tr><td colSpan={6} className="admin-emails-empty">This release was generated but never sent.</td></tr>
                            )}
                            {items.map((r) => {
                                const rowStatus = RECIPIENT_STATUS_META[r.status] || { label: r.status, badge: 'cold' };
                                const shape = SHAPE_META[r.shape] || { label: r.shape, hint: '' };
                                return (
                                    <tr key={r.id}>
                                        <td>
                                            <div className="admin-emails-name">{r.name || '—'}</div>
                                            <div className="admin-emails-subject">{r.email}</div>
                                        </td>
                                        <td>{r.organization_name || '—'}</td>
                                        <td><span className={`digest-shape shape-${r.shape}`} title={shape.hint}>{shape.label}</span></td>
                                        <td>
                                            <span className={`badge ${rowStatus.badge}`}>{rowStatus.label}</span>
                                            {r.skip_reason && <span className="digest-note"> {r.skip_reason}</span>}
                                        </td>
                                        <td className="num">{r.open_count || 0}</td>
                                        <td className="admin-emails-date">{fmtDate(r.sent_at)}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
};

const DigestsPage = () => {
    const { monthId } = useParams();
    return (
        <PageShell wrap={false}>
            <AdminNav />
            <div className="fs admin-emails-page">
                <EmailsTabs />
                {monthId ? <MonthDetail monthId={monthId} /> : <MonthList />}
            </div>
        </PageShell>
    );
};

export default DigestsPage;
